import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "react-oidc-context";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { syncEngine } from "../local/sync";
import { useCatalogDefs } from "../hooks/useCatalogDefs";
import {
  setManualRideServerId,
  subscribeManualRides,
} from "../store/manualRides";
import { shouldSuppressAuthRenew } from "./authRenewGate";
import { unsyncedManualRideCount } from "./manualRideSyncStatus";

const STORAGE_KEY = "scribe-fe.scoreContext";

const ScoreContext = createContext(null);

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function persist(state) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be unavailable (private browsing, etc.) — non-fatal.
  }
}

// #519 — full sign-out must drop the persisted scribe session (show/role) so the
// next login starts clean at the picker. #480 persists this across the Cognito
// logout round-trip for the "same ring device, sign in/out all day" case; the
// explicit "Sign out" action is the deliberate end-of-session escape hatch and
// clears it.
export function clearPersistedSession() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage may be unavailable (private browsing, etc.) — non-fatal.
  }
}

export function ScoreProvider({ children }) {
  const initial = loadInitial() ?? {};
  // #627 — the session is the ORG (via ActiveOrgProvider) + the SHOW. Judge
  // position is NOT part of it: it belongs to the class the scribe is assigned
  // to (Class.scribeAssignments, #396), and is derived per class/ride.
  const [selectedShow, setSelectedShowState] = useState(
    initial.selectedShow ?? null
  );
  // #258 — the user's currently-active role (e.g. SCRIBE / ANNOUNCER).
  const [activeRole, setActiveRoleState] = useState(initial.activeRole ?? null);
  // #628 — the PIN lock is the only way to change org/show. Unlocking (from any
  // screen) sets this, which is what opens the pickers on /setup. Deliberately
  // NOT persisted: a reload re-locks.
  const [pickerUnlocked, setPickerUnlocked] = useState(false);

  // #420 — effective connectivity = the browser is online AND the user hasn't
  // forced offline via the banner toggle. `browserOnline` follows
  // navigator.onLine + window online/offline; `forcedOffline` is the banner
  // switch. Both are pushed into the sync engine, which is the single place
  // that decides whether to talk to the network.
  const [browserOnline, setBrowserOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine,
  );
  const [forcedOffline, setForcedOffline] = useState(false);
  const [pendingSync, setPendingSync] = useState(0);
  // #894 — of the pendingSync total, how many are wedged in the engine's own
  // error/backoff retry (sync.js's errorCount): these are NOT actively
  // draining, so the indicator must say so rather than reading "Syncing N…"
  // forever while it silently retries every backoff window.
  const [errorSync, setErrorSync] = useState(0);
  // #1921 — of errorSync, how many have reached the engine's terminal
  // (give-up) state: a permanent 4xx that will never retry again on its
  // own. Separate flag so the chip can drop "retrying…" for these without
  // lying about the ones that genuinely still are.
  const [terminalSync, setTerminalSync] = useState(0);
  // #1923 — of pendingSync, how many are HELD: owned by a sub other than
  // whoever is signed in now, or by no one known (a pre-#1923 row). Held ops
  // are never sent under this session's token and never dropped — the same
  // "surface it, route discard through the 1921 detail view" contract as
  // errorSync/terminalSync above.
  const [heldSync, setHeldSync] = useState(0);
  // #2031 AC3 — of pendingSync, how many are BLOCKED: a dependent (tempId)
  // op whose own createRide op is itself genuinely stuck. Kept apart from
  // heldSync: this is an ORDERING gate (#882), not an ownership one, so it
  // gets its own count/wording rather than reusing "belongs to a previous
  // session", which would be false for it.
  const [blockedSync, setBlockedSync] = useState(0);
  // #1289 — a 401 whose silent renew ALSO failed. Not the raw 401: that's
  // handled invisibly. This is the one state a scribe has to act on, so it gets
  // its own flag rather than being folded into errorSync's red chip.
  // #1562 — a 403 never sets this: renewing can't fix a refused principal, so
  // it's classified apart in sync.js and surfaces via errorSync instead.
  const [authExpired, setAuthExpired] = useState(false);
  // #882 — rides (not sheets/ops) that haven't reached score-api yet, so the
  // scribe kiosk can show a "N unsynced rides" indicator that survives even
  // the case where the createRide op never made it into the outbox at all
  // (manualRides.cacheAndQueue's IndexedDB write failing) — see that module
  // for why this is sourced from the localStorage mirror, not the outbox.
  const [manualRidesList, setManualRidesList] = useState([]);
  // #1980 — entityKeys of createRide ops the sync-issue chip already
  // reports (see local/sync.js's snapshot + manualRideSyncStatus.js); a ride
  // in this set is excluded from unsyncedRideCount below so the two chips
  // stop double-reporting the same stuck write.
  const [erroredRideKeys, setErroredRideKeys] = useState(() => new Set());
  const unsyncedRideCount = useMemo(
    () => unsyncedManualRideCount(manualRidesList, erroredRideKeys),
    [manualRidesList, erroredRideKeys],
  );
  const online = browserOnline && !forcedOffline;

  // card #1664 — app-init hydration of the global test catalog (GET
  // /catalog) into IndexedDB; return value unused here (classDisplay.js's
  // resolveCatalogTestName reads the same cache directly via each
  // consuming component's own useCatalogDefs() call, e.g. ClassList/
  // RideList/ScoringForm). ScoreProvider mounts on every route (including
  // pre-session ones like /setup), so this is the "app-init" trigger point
  // — same shape as scoreadmin-fe's AdminShell (#1662). Hydration failure
  // (e.g. offline, no prior cache) is caught inside the hook itself and
  // never reaches here or blocks anything: each caller's own
  // caller-supplied fallback (a ride's own Test row, #1665) covers the
  // cold-start case (card description, "demotion in priority order, not a
  // swap").
  useCatalogDefs();

  // Thin adapter over the score-common clients (JSend-unwrapped) that the
  // framework-agnostic sync engine drains + hydrates through.
  const clients = useScoreApi();
  const auth = useAuth();
  const apiAdapter = useMemo(
    () => ({
      createRide: async (payload) => unwrap(await clients.rides.createRide(payload)),
      createScoreSheet: async (body) =>
        unwrap(await clients.scoreSheets.createScoreSheet(body)),
      // #2409 — used by the sync engine to adopt the sheet named by a
      // createScoreSheet 409's existingSheetId, so a replayed queued create
      // that lost the uniqueness race still ends up with the real sheet
      // (and its scores) cached locally instead of nothing.
      getScoreSheet: async (id) =>
        unwrap(await clients.scoreSheets.getScoreSheet(id)),
      updateScoreSheet: async (id, body) =>
        unwrap(await clients.scoreSheets.updateScoreSheet(id, body)),
      completeScoreSheet: async (id) =>
        unwrap(await clients.scoreSheets.completeScoreSheet(id)),
      // #698 — one-way marks freeze; POST /scoresheet/:id/confirm-marks.
      confirmScoreSheetMarks: async (id) =>
        unwrap(await clients.scoreSheets.confirmScoreSheetMarks(id)),
      // #1751 — one panel judge signs a panel-consensus sheet; POST
      // /scoresheet/:id/panel-signature. `judgeId` here is the PANEL judge's
      // roster row id, not the sheet's booth position.
      panelSignScoreSheet: async (id, judgeId) =>
        unwrap(await clients.scoreSheets.signScoreSheetPanel(id, { judgeId })),
      // card #3005 — sync.js's hydrate() forwards its own AbortController
      // signal into each of these so an unmount actually cancels an
      // in-flight hydration walk instead of it running to completion
      // against a torn-down test server / dead component tree.
      listShows: async (signal) =>
        unwrap(await clients.shows.listShows({ signal })),
      listClasses: async (showId, signal) =>
        unwrap(await clients.classes.listClasses(showId, undefined, { signal })),
      listRides: async (classId, signal) =>
        unwrap(
          await clients.rides.listRides(
            classId,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            { signal },
          ),
        ),
      getTest: async (testId, signal) =>
        unwrap(await clients.tests.getTest(testId, { signal })),
      listPeople: async (signal) =>
        unwrap(await clients.people.listPeople(undefined, { signal })),
      listHorses: async (signal) =>
        unwrap(await clients.horses.listHorses({ signal })),
      // card #139 — utility events (BLOOD/LAMENESS), queued through the same
      // outbox as scores so a raise/clear made offline isn't dropped.
      raiseUtilityEvent: async (rideId, type) =>
        unwrap(await clients.rides.createRideUtilityEvent(rideId, { type })),
      clearUtilityEvent: async (rideId, eventId) =>
        unwrap(await clients.rides.deleteRideUtilityEvent(rideId, eventId)),
      // #3088 - the REVIEW button: send the ride to the review queue.
      requestRideReview: async (rideId, comment) =>
        unwrap(await clients.rides.requestRideReview(rideId, { comment })),
      // #2812 — the sync engine's quarantine fallback (see sync.js's
      // flush()). Goes through the generated QuarantineApi client, same as
      // every other write here, never a direct fetch.
      quarantinePayload: async (body) =>
        unwrap(await clients.quarantine.quarantinePayload(body)),
    }),
    [clients],
  );

  useEffect(() => {
    syncEngine.setApi(apiAdapter);
  }, [apiAdapter]);

  // #1923 — the signed-in Cognito sub, pushed into the engine (which forwards
  // it to db.js) on every auth change: sign-in, sign-out (auth.user becomes
  // null → sub null), or a user swap on a shared kiosk. This is what lets
  // enqueue() stamp new ops with the right owner and lets flush() hold ops
  // that belong to someone else instead of replaying them under this token.
  const sub = auth.user?.profile?.sub ?? null;
  useEffect(() => {
    syncEngine.setCurrentOwnerSub(sub);
  }, [sub]);

  // #1289 — hand the engine an OIDC-backed silent renew. The engine is
  // framework-agnostic and owns no auth; it just asks "can you fix this?" when
  // an op 401s. Resolving false (rather than throwing) is what escalates to the
  // session-expired prompt. Deliberately does NOT redirect: a hard redirect
  // mid-ride would take the scribe off the sheet they're writing on. Only the
  // scribe's own tap does that.
  const { signinSilent } = auth;
  useEffect(() => {
    syncEngine.setAuthFailureHandler(async () => {
      // #1293 — under Cypress this is suppressed (see authRenewGate.js): a
      // spec with no auth intercepts of its own must never let an incidental
      // 401 fire a real signinSilent() exchange against the Cognito
      // authority. Resolving false here is the same "renew unavailable"
      // outcome handleAuthFailure already gives a null handler.
      if (shouldSuppressAuthRenew()) return false;
      try {
        const user = await signinSilent();
        return Boolean(user?.access_token);
      } catch {
        return false;
      }
    });
    return () => syncEngine.setAuthFailureHandler(null);
  }, [signinSilent]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const goOnline = () => setBrowserOnline(true);
    const goOffline = () => setBrowserOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // Feed the effective-online flags into the engine; a transition to online
  // makes it drain the outbox + hydrate the cache.
  useEffect(() => {
    syncEngine.setBrowserOnline(browserOnline);
  }, [browserOnline]);
  useEffect(() => {
    syncEngine.setForcedOffline(forcedOffline);
  }, [forcedOffline]);

  // Engine lifecycle + pendingSync subscription. On first mount (if online) do
  // one sync() so the cache is fresh without waiting for a reconnect.
  useEffect(() => {
    const unsubscribe = syncEngine.subscribe((s) => {
      setPendingSync(s.pendingSync);
      setErrorSync(s.errorSync);
      setTerminalSync(s.terminalSync ?? 0);
      setHeldSync(s.heldSync ?? 0);
      setBlockedSync(s.blockedSync ?? 0);
      setAuthExpired(Boolean(s.authExpired));
      // #1980 — kept in its own piece of state (rather than folded straight
      // into unsyncedRideCount here) so a change to EITHER this or the
      // manual-rides list below recomputes the same derived count; see the
      // useMemo above.
      setErroredRideKeys(s.erroredRideKeys ?? new Set());
    });
    // When a temp manual ride gets its server id, stamp the localStorage mirror
    // so open scoring screens resolve it (#386/#422).
    const unsubscribeRide = syncEngine.onRideSynced((tempId, serverId) =>
      setManualRideServerId(tempId, serverId),
    );
    syncEngine.start();
    // Fire-and-forget mount sync. hydrate() intentionally rethrows a REAL
    // listShows failure (sync.js, card #3005) so a genuine re-fetch is never
    // reported as success. This catch only logs it (and keeps it from
    // becoming an unhandled rejection, which a real browser merely logs but
    // vitest fails the run on): nothing in this component surfaces it —
    // the picker screen's InitialSetup does its own show-list fetch and
    // renders its own error/offline copy off that (#1746/#2196/#3029). #3050.
    if (syncEngine.isOnline()) void syncEngine.sync().catch((err) => console.warn("[sync] mount sync failed", err));
    // #882 — live "N unsynced rides" count off the same localStorage writes
    // that drive the manual-rides UI (add, serverId stamp, retry).
    const unsubscribeManualRides = subscribeManualRides((list) =>
      setManualRidesList(list),
    );
    return () => {
      unsubscribe();
      unsubscribeRide();
      unsubscribeManualRides();
      syncEngine.stop();
    };
  }, []);

  // Persist the session fields whenever any of them change.
  useEffect(() => {
    persist({ selectedShow, activeRole });
  }, [selectedShow, activeRole]);

  const startSession = useCallback((show) => {
    setSelectedShowState(show);
    // Picking a show consumes the unlock — the pickers close behind you (#628).
    setPickerUnlocked(false);
  }, []);

  const unlockPicker = useCallback(() => setPickerUnlocked(true), []);
  const lockPicker = useCallback(() => setPickerUnlocked(false), []);

  const endSession = useCallback(() => {
    setSelectedShowState(null);
  }, []);

  // #521 — switch the active show. Bridged from the app-bar ShowSwitcher.
  const setSelectedShow = useCallback((show) => {
    setSelectedShowState(show);
  }, []);

  const setActiveRole = useCallback((role) => {
    setActiveRoleState(role);
  }, []);

  // Banner toggle → flip the forced-offline flag. Also pokes the sync engine
  // SYNCHRONOUSLY with the new value here (not just via the passive effect at
  // line ~129) so `syncEngine.isOnline()` is correct in the same tick the
  // chip/Directives DOM updates — otherwise there's a desync window where the
  // UI says "Offline"/"Online" but the engine hasn't flipped yet, which is
  // flaky on slow CI (a persist can race the old engine value, or the
  // reconnect drain can start late). The effect at 129-131 stays as an
  // idempotent backstop (it also covers browserOnline transitions).
  const toggleOnline = useCallback(
    () =>
      setForcedOffline((v) => {
        const next = !v;
        syncEngine.setForcedOffline(next);
        return next;
      }),
    [],
  );

  const value = useMemo(
    () => ({
      selectedShow,
      activeRole,
      online,
      pendingSync,
      errorSync,
      terminalSync,
      heldSync,
      blockedSync,
      authExpired,
      unsyncedRideCount,
      // #1980 — exposed so any consumer that reads the raw manual-rides list
      // itself (UnsyncedRidesIndicator.jsx's popover) can apply the SAME
      // exclusion the count above already applies, rather than filtering the
      // list a different way and disagreeing with its own chip's count.
      erroredRideKeys,
      pickerUnlocked,
      isSessionReady: Boolean(selectedShow),
      startSession,
      endSession,
      setSelectedShow,
      setActiveRole,
      toggleOnline,
      unlockPicker,
      lockPicker,
    }),
    [
      selectedShow,
      activeRole,
      online,
      pendingSync,
      errorSync,
      terminalSync,
      heldSync,
      blockedSync,
      authExpired,
      unsyncedRideCount,
      erroredRideKeys,
      pickerUnlocked,
      startSession,
      endSession,
      setSelectedShow,
      setActiveRole,
      toggleOnline,
      unlockPicker,
      lockPicker,
    ]
  );

  return (
    <ScoreContext.Provider value={value}>{children}</ScoreContext.Provider>
  );
}

export function useScoreContext() {
  const ctx = useContext(ScoreContext);
  if (!ctx) {
    throw new Error("useScoreContext must be used within a ScoreProvider");
  }
  return ctx;
}
